import type { JsonRecord, WorkContext } from "../../types";
import { schema } from "../../lib/supabase";
import type { ExecutionListResult, ExecutionOperation, ExecutionTypeKey } from "./executionTypes";

export interface ConcreteCatalogs {
  fronts: JsonRecord[];
  subfronts: JsonRecord[];
  mixtures: JsonRecord[];
  specifications: JsonRecord[];
  pressureUnits: JsonRecord[];
  areas: JsonRecord[];
  structures: JsonRecord[];
  steelSpecifications: JsonRecord[];
}

function message(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

async function call(
  operation: ExecutionOperation,
  context: WorkContext,
  type: ExecutionTypeKey,
  data: JsonRecord,
) {
  if (type === "acero_estructural") {
    const result = await schema("obra").rpc("api_suministro_acero_estructural" as never, {
      p_operacion: operation,
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
      p_datos: data,
    } as never);
    message(result.error);
    return result.data as unknown;
  }
  // Contrato único del módulo: catalogos, listar, insertar, editar, eliminar y
  // validar se discriminan con p_operacion/p_tipo sobre esta misma RPC.
  const result = await schema("obra").rpc("api_ejecucion_obra" as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_tipo: type,
    p_datos: data,
  } as never);

  message(result.error);
  return result.data as unknown;
}

function normalize(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function canInsertFromContext(context: WorkContext) {
  const role = normalize(context.rol);
  if (role === "admin") return true;

  if (!["coordinador", "supervisor", "auxiliar"].includes(role)) return false;

  const area = normalize(context.area_nombre);
  return ["calidad", "control de obra", "supervision de obra"].includes(area);
}

function displayValue(value: unknown) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

function formatStation(value: unknown) {
  if (value == null || value === "") return "";

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return displayValue(value);

  const sign = numeric < 0 ? "-" : "";
  const absolute = Math.abs(numeric);
  const meters = Math.floor(absolute);
  const decimals = absolute - meters;
  const kilometer = Math.floor(meters / 1000);
  const remainder = meters % 1000;
  const decimalText = decimals > 0
    ? String(Number(decimals.toFixed(3))).replace(/^0/, "")
    : "";

  return `${sign}${String(kilometer).padStart(3, "0")}+${String(remainder).padStart(3, "0")}${decimalText}`;
}

function rangeText(start: string, end: string) {
  if (start && end) return `${start} al ${end}`;
  return start || end;
}

function formatTerraceriaUbicacion(row: JsonRecord) {
  const normalizedFranja = typeof row.franja === "string" ? row.franja.trim() : "";
  const franjaSuffix = normalizedFranja ? `; franja ${normalizedFranja}` : "";
  const workType = normalize(row.tipo_obra);

  if (workType === "lineal") {
    const location = rangeText(formatStation(row.cad_inicial), formatStation(row.cad_final));
    return location ? `${location}${franjaSuffix}` : normalizedFranja ? `franja ${normalizedFranja}` : "";
  }

  if (workType === "acotado") {
    const location = rangeText(displayValue(row.punto_inicial), displayValue(row.punto_final));
    return location ? `${location}${franjaSuffix}` : normalizedFranja ? `franja ${normalizedFranja}` : "";
  }

  if (workType === "puntual") {
    const location = displayValue(row.ubicacion_puntual);
    return location ? `${location}${franjaSuffix}` : normalizedFranja ? `franja ${normalizedFranja}` : "";
  }

  // Compatibilidad temporal durante la migración de los registros históricos
  // de Superficie a Puntual.
  const legacyLocation = displayValue(row.ubicacion_puntual) || displayValue(row.ubicacion);
  return legacyLocation ? `${legacyLocation}${franjaSuffix}` : normalizedFranja ? `franja ${normalizedFranja}` : "";
}

function enrichRows(rows: JsonRecord[], type: ExecutionTypeKey) {
  return rows.map((row) => {
    const unit = displayValue(row.resistencia_unidad_simbolo);
    const resistance = row.resistencia == null || row.resistencia === ""
      ? ""
      : `${String(row.resistencia)}${unit ? ` ${unit}` : ""}`;
    const volume = row.volumen == null || row.volumen === ""
      ? ""
      : `${String(row.volumen)} m³`;

    const product = displayValue(row.producto);
    let steelDesignation = "";
    if (product === "Perfil") steelDesignation = displayValue(row.perfil);
    if (product === "Placa" && [row.ancho_mm, row.largo_mm, row.espesor_mm].some((value) => value != null && value !== "")) {
      steelDesignation = `${displayValue(row.ancho_mm) || "—"} × ${displayValue(row.largo_mm) || "—"} × ${displayValue(row.espesor_mm) || "—"} mm`;
    }
    if (product === "Barra") {
      const section = displayValue(row.seccion_transversal);
      if ((section === "Circular" || section === "Hexagonal") && row.diametro_mm != null && row.diametro_mm !== "") steelDesignation = `${section} — Ø ${row.diametro_mm} mm`;
      if (section === "Cuadrada" && row.lado_mm != null && row.lado_mm !== "") steelDesignation = `Cuadrada — lado ${row.lado_mm} mm`;
    }
    const numericQuantity = Number(row.cantidad);
    const quantity = row.cantidad == null || row.cantidad === "" ? "" : Number.isFinite(numericQuantity)
      ? numericQuantity.toLocaleString("es-MX", { maximumFractionDigits: 3 })
      : displayValue(row.cantidad);
    const quantityUnit = row.unidad_cantidad === "kg"
      ? "kg"
      : row.unidad_cantidad === "t"
        ? "t"
        : row.unidad_cantidad === "pieza"
          ? (Number(row.cantidad) === 1 ? "pieza" : "piezas")
          : displayValue(row.unidad_cantidad);
    return {
      ...row,
      volumen_renderizado: volume,
      resistencia_renderizada: resistance,
      estatus_validacion: row.estatus_validacion
        ?? (row.validated_at != null ? "Validado" : "Pendiente de validación"),
      estado_revision: row.validated_at != null || row.estatus_validacion === "Validado"
        ? "Validado"
        : "Pendiente de validación",
      ubicacion_renderizada: type === "terracerias" ? formatTerraceriaUbicacion(row) : row.ubicacion,
      ...(type === "acero_estructural" ? {
        designacion_renderizada: steelDesignation,
        cantidad_renderizada: quantity ? `${quantity}${quantityUnit ? ` ${quantityUnit}` : ""}` : "",
      } : {}),
    };
  });
}

export async function listExecution(
  context: WorkContext,
  type: ExecutionTypeKey,
  filters: JsonRecord,
): Promise<ExecutionListResult> {
  const raw = await call("listar", context, type, filters);
  const value = (raw || {}) as JsonRecord;
  const rawRows = Array.isArray(raw)
    ? raw as JsonRecord[]
    : Array.isArray(value.items)
      ? value.items as JsonRecord[]
      : Array.isArray(value.registros)
        ? value.registros as JsonRecord[]
        : Array.isArray(value.rows) ? value.rows as JsonRecord[] : [];
  const rows = enrichRows(rawRows, type);

  return {
    rows,
    total: Number(value.total ?? rows.length),
    fronts: arrayFrom(value, "frentes"),
    subfronts: arrayFrom(value, "subfrentes"),
    canInsert: typeof value.puede_insertar === "boolean"
      ? value.puede_insertar
      : type === "acero_estructural" ? false : canInsertFromContext(context),
  };
}

const arrayFrom = (value: JsonRecord, ...keys: string[]) => {
  for (const key of keys) if (Array.isArray(value[key])) return value[key] as JsonRecord[];
  return [];
};

/** Catálogos ya limitados por proyecto, contrato y permisos por el RPC de Ejecución. */
export async function loadExecutionCatalogs(
  context: WorkContext,
  type: ExecutionTypeKey,
): Promise<ConcreteCatalogs> {
  const raw = await call("catalogos", context, type, {});
  const value = (raw || {}) as JsonRecord;
  const catalogValue = value.catalogos && typeof value.catalogos === "object" ? value.catalogos as JsonRecord : value;
  return {
    fronts: arrayFrom(catalogValue, "frentes"),
    subfronts: arrayFrom(catalogValue, "subfrentes"),
    mixtures: arrayFrom(value, "tipos_mezcla"),
    specifications: arrayFrom(value, "especificaciones"),
    pressureUnits: arrayFrom(value, "unidades_presion"),
    areas: arrayFrom(value, "areas"),
    structures: arrayFrom(value, "estructuras"),
    steelSpecifications: arrayFrom(catalogValue, "especificaciones"),
  };
}

const concreteKeys = [
  "area_id", "frente_id", "subfrente_id", "fecha", "elemento", "ubicacion",
  "proveedor", "actividad_material_id", "especificacion_id", "resistencia",
  "resistencia_unidad_id", "volumen", "cantidad_entregas", "garantia", "muestras_co",
  "ctrl_fresco_co", "sup_ext", "comentarios",
] as const;

function concretePayload(context: WorkContext, data: JsonRecord, includeId = false) {
  const payload: JsonRecord = {};
  if (includeId && data.id) payload.id = data.id;
  for (const key of concreteKeys) {
    if (key === "area_id" && normalize(context.rol) !== "admin") continue;
    payload[key] = data[key] ?? null;
  }
  return payload;
}

const structuralSteelKeys = [
  "fecha_suministro", "folio_remision_proveedor", "folio_nota_recepcion", "frente_id", "subfrente_id",
  "producto", "especificacion_id", "grado", "perfil", "seccion_transversal", "ancho_mm", "largo_mm",
  "espesor_mm", "diametro_mm", "lado_mm", "colada", "lote", "cantidad", "unidad_cantidad", "proveedor",
  "comentarios",
] as const;

function structuralSteelPayload(data: JsonRecord, includeId = false) {
  const payload: JsonRecord = {};
  if (includeId && data.id) payload.id = data.id;
  for (const key of structuralSteelKeys) payload[key] = data[key] === "" || data[key] === undefined ? null : data[key];
  if (data.producto !== "Perfil") payload.perfil = null;
  if (data.producto !== "Placa") payload.ancho_mm = payload.largo_mm = payload.espesor_mm = null;
  if (data.producto !== "Barra") payload.seccion_transversal = payload.diametro_mm = payload.lado_mm = null;
  if (data.producto === "Barra" && ["Circular", "Hexagonal"].includes(String(data.seccion_transversal))) payload.lado_mm = null;
  if (data.producto === "Barra" && data.seccion_transversal === "Cuadrada") payload.diametro_mm = null;
  return payload;
}

export const createExecution = (
  context: WorkContext,
  type: ExecutionTypeKey,
  data: JsonRecord,
) => call("insertar", context, type, type === "concreto"
  ? concretePayload(context, data)
  : type === "acero_estructural" ? structuralSteelPayload(data)
  : { ...data, area_id: data.area_id ?? context.area_id });

export const updateExecution = (
  context: WorkContext,
  type: ExecutionTypeKey,
  data: JsonRecord,
) => call("editar", context, type, type === "concreto"
  ? concretePayload(context, data, true)
  : type === "acero_estructural" ? structuralSteelPayload(data, true) : data);

export const deleteExecution = (
  context: WorkContext,
  type: ExecutionTypeKey,
  id: string,
) => call("eliminar", context, type, { id });

export const validateExecution = (context: WorkContext, type: ExecutionTypeKey, id: string) =>
  call("validar", context, type, { id });
